import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import Image from 'next/image'
import {
    Building2,
    Clock,
    Facebook,
    House,
    Instagram,
    MapPin,
    MapPinned,
    Phone,
    ShieldCheck,
    Star
} from 'lucide-react'

import { BUSINESS, EXTERIOR_ONLY } from '@/content/business'
import { AREA_LINKS, FOOTER_COMPANY, MAIN_NAV } from '@/content/navigation'

const SERVICE_COLUMNS = [
    {
        title: "Exterior Windows & Doors",
        links: [
            { label: "Exterior window replacement", href: '/services/window-replacement/' },
            { label: "Exterior door installation", href: '/services/door-installation/' },
            { label: "Window styles", href: '/window-options/double-hung-windows/' },
            { label: "Exterior storm windows", href: '/services/window-replacement/storm-windows/' },
            { label: "Entry doors", href: '/door-options/entry-doors/' },
            { label: "Storm doors", href: '/door-options/storm-doors/' }
        ]
    },
    ...MAIN_NAV.slice(1, 4).map(group => ({ title: group.columns[0].title, links: group.columns[0].links }))
]

const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='relative mt-auto overflow-hidden bg-[#0b0f14] text-white'>
            <div className='relative z-10 mx-auto max-w-7xl px-4 pb-6 pt-16 sm:px-6 lg:px-8'>
                <div className='grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,2.4fr)]'>
                    <div>
                        <Image
                            src='/images/acm/logo-dark.webp'
                            alt='The ACM Pros Exteriors'
                            width={194}
                            height={128}
                            className='h-24 w-auto'
                        />

                        <a href={BUSINESS.phoneHref} className='mt-6 inline-flex items-center gap-3 text-2xl font-bold text-white hover:underline'>
                            <Phone className='size-6 text-sky' aria-hidden='true' />
                            {BUSINESS.phone}
                        </a>

                        <p className='mt-5 text-lg font-semibold'>
                            {BUSINESS.footerTagline}
                        </p>

                        <p className='mt-3 text-base text-white/85'>
                            Family-owned exterior contractor based in {BUSINESS.base}, serving Huntsville and North Alabama since {BUSINESS.founded}.
                        </p>

                        <p className='mt-4 flex items-start gap-2 text-base text-white/85'>
                            <ShieldCheck className='mt-1 size-5 shrink-0 text-sky' aria-hidden='true' />
                            <span>{BUSINESS.licenseLabel}, held by {BUSINESS.legalName}. Insured for residential and commercial work.</span>
                        </p>

                        <p className='mt-3 flex items-start gap-2 text-base text-white/85'>
                            <MapPin className='mt-1 size-5 shrink-0 text-sky' aria-hidden='true' />
                            <span>Based in {BUSINESS.base}. We do not work in Tennessee.</span>
                        </p>

                        <p className='mt-5 rounded-xl border border-white/20 px-4 py-3 text-base'>
                            {EXTERIOR_ONLY.short} We do not install interior doors or interior windows.
                        </p>

                        <div className='mt-6'>
                            <p className='flex items-center gap-2 font-bold'>
                                <Clock className='size-5 text-sky' aria-hidden='true' />
                                Hours
                            </p>

                            <p className='text-white/85'>
                                Monday to Saturday, 7 AM to 7 PM
                                <br />
                                Closed Sunday
                            </p>
                        </div>

                        <ul className='mt-6 flex flex-wrap gap-x-5 gap-y-2'>
                            <li>
                                <a href={BUSINESS.googleReviewsUrl} target='_blank' rel='noopener noreferrer' className='inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-sky'>
                                    <Star className='size-5' aria-hidden='true' />
                                    Google reviews
                                </a>
                            </li>
                            <li>
                                <a href={BUSINESS.facebookUrl} target='_blank' rel='noopener noreferrer' className='inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-sky'>
                                    <Facebook className='size-5' aria-hidden='true' />
                                    Facebook
                                </a>
                            </li>
                            <li>
                                <a href={BUSINESS.instagramUrl} target='_blank' rel='noopener noreferrer' className='inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-sky'>
                                    <Instagram className='size-5' aria-hidden='true' />
                                    Instagram
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div className='grid gap-10 sm:grid-cols-2 lg:grid-cols-3'>
                        <div className='sm:col-span-2 lg:col-span-1'>
                            <h2 className='flex items-center gap-2.5 border-b border-white/20 pb-3 text-xl font-bold text-white'>
                                <House className='size-5 text-sky' aria-hidden='true' />
                                Services
                            </h2>

                            <div className='mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-1'>
                                {SERVICE_COLUMNS.map(column => (
                                    <div key={column.title}>
                                        <h3 className='text-lg font-bold text-sky'>
                                            {column.title}
                                        </h3>

                                        <ul className='mt-2'>
                                            {column.links.map(link => (
                                                <li key={link.href}>
                                                    <Link href={link.href} className='block py-1.5 text-base text-white/90 hover:text-white hover:underline'>
                                                        {link.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h2 className='flex items-center gap-2.5 border-b border-white/20 pb-3 text-xl font-bold text-white'>
                                <MapPinned className='size-5 text-sky' aria-hidden='true' />
                                Areas we serve
                            </h2>

                            <ul className='mt-4'>
                                {AREA_LINKS.map(link => (
                                    <li key={link.href}>
                                        <Link href={link.href} className='flex items-start gap-2 py-1.5 text-base text-white/90 hover:text-white hover:underline'>
                                            <MapPin className='mt-1 size-4 shrink-0 text-sky' aria-hidden='true' />
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h2 className='flex items-center gap-2.5 border-b border-white/20 pb-3 text-xl font-bold text-white'>
                                <Building2 className='size-5 text-sky' aria-hidden='true' />
                                Company
                            </h2>

                            <ul className='mt-4'>
                                {FOOTER_COMPANY.map(link => (
                                    <li key={link.href}>
                                        <Link href={link.href} className='block py-1.5 text-base text-white/90 hover:text-white hover:underline'>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

            </div>

            <div className='relative'>
                <Image
                    src='/images/acm/footer.webp'
                    alt=''
                    width={1400}
                    height={789}
                    sizes='100vw'
                    className='block h-[clamp(10rem,17vw,15rem)] w-full object-cover object-[center_88%]'
                />

                <div aria-hidden='true' className='absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-[#0b0f14] to-transparent' />

                <div className='absolute inset-x-0 bottom-0'>
                    <div className='mx-auto flex max-w-7xl flex-wrap justify-between gap-x-6 gap-y-2 border-t border-white/15 px-4 py-5 text-base text-white/90 sm:px-6 lg:px-8'>
                        <span>
                            &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> {BUSINESS.legalName}. All rights reserved.
                        </span>

                        <span>
                            Built for North Alabama homes since {BUSINESS.founded}
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    )
}

export default SiteFooter
